<template>
    <div>
        <div class="actions d-flex justify-content-between">
            <div class="director-actions-btn">
                <b-button
                    v-if="directorsList.enstonDirectorsForDropdown.length <= 0 && directorsList.directorsDropdownList.length <= 0"
                    variant="primary"
                    class="d-flex justify-content-center align-items-center"
                    data-test="add-a-new-director-btn"
                    @click="openCreateDirectorForm"
                >
                    <vector
                        data-name="add-user"
                        data-width="15px"
                        data-height="12px"
                        class="mr-.5 d-flex justify-content-center align-items-center"
                    />
                    Add a new director
                </b-button>

                <b-dropdown
                    v-else
                    id="dropdown-1"
                    class="mt-.3"
                    variant="primary"
                    data-test="add-a-new-director-dropdown"
                >
                    <template #button-content>
                        <div
                            class="
                                d-inline-flex
                                justify-content-center
                                align-items-center
                            "
                        >
                            <vector
                                data-name="add-user"
                                data-width="15px"
                                data-height="12px"
                                class="
                                    mr-.5
                                    d-flex
                                    justify-content-center
                                    align-items-center
                                "
                            />
                            Add another director
                        </div>
                    </template>

                    <b-dropdown-item
                        style="font-size: 1.2em"
                        variant="primary"
                        data-test="add-a-new-director-manually"
                        @click="openCreateDirectorForm"
                    >
                        Add a new director manually
                    </b-dropdown-item>

                    <div
                        v-if="
                            directorsList.directorsDropdownList &&
                            directorsList.directorsDropdownList.length >= 1
                        "
                    >
                        <div class="dropdown-divider"></div>

                        <b-dropdown-text class="text-ternary">
                            <span> Select one from the list </span>
                        </b-dropdown-text>

                        <b-dropdown-item
                            v-for="director in directorsList.directorsDropdownList"
                            :key="director.name + '_' + director.email + '_' + director._id"
                            class="text-primary text-uppercase"
                            variant="primary"
                            style="font-size: 1.2em"                    
                            :data-test="`director-${director.email}`"
                            @click="$emit('createDirectorFromDropdown', director)"
                        >
                            {{ director.name }}
                        </b-dropdown-item>
                    </div>

                    <div v-if="directorsList.enstonDirectorsForDropdown.length >= 1">
                        <div class="dropdown-divider"></div>

                        <b-dropdown-text class="text-ternary">
                            <span> Select from nominee directors list </span>
                        </b-dropdown-text>

                        <b-dropdown-item
                            v-for="nomineeDirector in directorsList.enstonDirectorsForDropdown"
                            :key="nomineeDirector.name"
                            class="text-primary text-uppercase"
                            variant="primary"
                            style="font-size: 1.2em"
                            data-test="nominee-director"
                            @click="createNomineeDirector(nomineeDirector)"
                        >
                            {{ nomineeDirector.name }}
                        </b-dropdown-item>
                    </div>
                </b-dropdown>
            </div>
        </div>

        <loader v-if="isFetchingData" data-type="MinimumTaskList" class="mt-1 custom-loading" />

        <div v-if="allDirectors && ! isFetchingData" data-test="directors-list">
            <div
                v-for="director in allDirectors"
                :key="director.name"
                class="mt-2"
            >
                <div
                    data-test="list-box"
                    class="list-box mb-1 row align-items-center"
                    style="margin-left: 0.1em; margin-right: 0.1em"
                >
                    <div
                        class="
                            user
                            d-flex
                            align-items-center
                            col-lg-4
                            justify-content-lg-start justify-content-md-center
                            mb-md-0 mb-sm-1
                        "
                    >
                        <img
                            v-if="director.is_nominee_director && director.is_enston_director"
                            src="/logo/Enston-favicon.png"
                            alt="Enston Logo"
                            width="30px"
                            height="30px"
                        />

                        <b-avatar
                            v-else
                            size="30"
                            :text="getAvatarInitial(director.name)"
                            :variant="`light-primary`"
                        />

                        <h3 class="font-weight-bold p-0 m-0 ml-1 text-break">
                            {{ director.name }}
                        </h3>
                    </div>

                    <p
                        class="
                            m-0
                            p-0
                            col-lg-5
                            text-right
                            align-items-center
                            text-sm-center
                            mb-md-0 mb-sm-1
                            text-lg-right
                        "
                    >
                        {{ director.email }}
                    </p>

                    <div
                        data-test="actions"
                        class="
                            actions
                            mb-md-0
                            d-flex
                            justify-content-md-end
                            align-items-center
                            col-lg-3
                            justify-content-sm-center
                        "
                    >
                        <span
                            v-if="! director.is_nominee_director"
                            :class="[
                                'steps-status d-block mr-2',
                                getCompletedStepsOf(director) === getTotalStepsOf(director)
                                    ? 'text-success'
                                    : 'text-danger',
                            ]"
                        >
                            {{ getCompletedStepsOf(director) }} / {{ getTotalStepsOf(director) }}
                        </span>

                        <div
                            class="mr-2 cursor-pointer"
                            :data-test="`edit-${director.name}`"
                            @click="$emit('edit', director, 'edit')"
                        >
                            <feather-icon size="21" icon="EditIcon" />
                        </div>

                        <div class="cursor-pointer">
                            <vector
                                data-type="icon"
                                data-name="cancel"
                                :data-test="`delete-${director.name}`"
                                @click.native="$emit('remove', director)"
                            ></vector>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    
</template>

<script>
import {
    BDropdown,
    BDropdownItem,
    BDropdownText,
    BAvatar,
    BButton,
} from "bootstrap-vue";
import { NOMINEE_DIRECTOR } from "@/constants/config";

export default {
    components: {
        BDropdown,
        BDropdownItem,
        BDropdownText,
        BAvatar,
        BButton
    },

    props: {
        directorsList: {
            type: Object,
            required: true,
        },

        directorBluePrint: {
            type: Object,
            required: true,
        },

        isFetchingData: {
            type: Boolean,
            required: true
        }
    },

    data() {
        return {
            totalSteps: Object.keys(this.directorBluePrint).length - 1,
        };
    },

    computed: {
        allDirectors() {
            return this.directorsList.directors.concat(
                this.directorsList.nomineeDirectors
            );
        },
    },

    methods: {
        openCreateDirectorForm(director = this.directorBluePrint) {
            this.$emit("createDirector", director);
        },

        getTotalStepsOf(director) {
            if(director.isNomineeDirector && director.isNewNominator) {
                // ignore _id, remark, cessation, isNomineeDirector, alias, and corporate fields,
                // ignore 3 new nominator fields(isNewNominator, nominatorEmail, nominator) 
                // ignore generatePOA
                // ignore dateOfExpiry and nominatorDateOfExpiry
                if(director.nominatorType?.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.INDIVIDUAL)
                    return Object.keys(this.directorBluePrint).length - (1 + 1 + 1 + 1 + 1 + 5 + 3 + 1 + 2);

                // ignore _id, remark, alias, cessation and isNomineeDirector
                // ignore 6 new nominator fields(isNewNominator, nominatorEmail, nominator, nominatorDOB, nominatorIdType, nominatorNationality) 
                // ignore generatePOA,
                // ignore dateOfExpiry and nominatorDateOfExpiry
                return Object.keys(this.directorBluePrint).length - (1 + 1 + 1 + 1 + 1 + 6 + 1 + 2);
            }

            if(director.isNomineeDirector && !director.isNewNominator) {
                // ignore _id, remark, cessation, isNomineeDirector, alias, and corporate fields, 8 new nominator fields
                // ignore generatePOA,
                // ignore dateOfExpiry and nominatorDateOfExpiry
                if(director.nominatorType?.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.INDIVIDUAL)
                    return Object.keys(this.directorBluePrint).length - (1 + 1 + 1 + 1 + 1 + 5 + 8 + 1 + 2);

                // ignore _id, remark, alias, cessation and isNomineeDirector, 8 new nominator fields
                // ignore generatePOA,
                // ignore dateOfExpiry and nominatorDateOfExpiry
                return Object.keys(this.directorBluePrint).length - (1 + 1 + 1 + 1 + 1 + 8 + 1 + 2);
            }
            
            // ignore _id, remark, cessation, appointment, uen, legal form, jurisdiction, statue, registrar
            // nominator name, alias, isNomineeDirector, nominator type,  8 new nominator fields
            // ignore generatePOA,
            // ignore dateOfExpiry and nominatorDateOfExpiry
            return Object.keys(this.directorBluePrint).length - (1 + 12 + 8 + 1 + 2);
        },

        getCompletedStepsOf(director) {
            if(director.isNomineeDirector && director.isNewNominator) {
                if(director.nominatorType.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.INDIVIDUAL)
                    return (
                        Object.keys(this.directorBluePrint).filter(
                            key => 
                                !!director[key] &&
                                !key.includes('isNomineeDirector') &&
                                !key.includes('dateOfExpiry') &&
                                !key.includes('cessationDate') &&
                                !key.includes('nominatorUen') &&
                                !key.includes('nominatorLegalForm') &&
                                !key.includes('nominatorStatue') &&
                                !key.includes('nominatorRegistrar') &&
                                !key.includes('nominatorJurisdiction') &&
                                !key.includes('nominatorAlias') &&
                                !key.includes('remark') &&
                                !key.includes('generatePOA') &&
                                // ignore new nominator field
                                !key.includes('isNewNominator') &&
                                !key.includes('nominatorEmail') &&
                                !key.includes('nominatorDateOfExpiry') &&
                                key !== 'nominator'
                        ).length - 1
                    );

                return (
                    Object.keys(this.directorBluePrint).filter(
                        key => 
                            !!director[key] &&
                            !key.includes('isNomineeDirector') &&
                            !key.includes('dateOfExpiry') &&
                            !key.includes('nominatorAlias') &&
                            !key.includes('cessationDate') &&
                            !key.includes('remark') &&
                            !key.includes('generatePOA') &&
                            // ignore new nominator field
                            !key.includes('isNewNominator') &&
                            !key.includes('nominatorEmail') &&
                            !key.includes('nominatorDOB') &&
                            !key.includes('nominatorIdType') &&
                            !key.includes('nominatorNationality') &&
                            !key.includes('nominatorDateOfExpiry') &&
                            key !== 'nominator'
                    ).length - 1
                );
            }

            if(director.isNomineeDirector && !director.isNewNominator) {
                if(director.nominatorType.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.INDIVIDUAL)
                    return (
                        Object.keys(this.directorBluePrint).filter(
                            key => 
                                !!director[key] &&
                                !key.includes('isNomineeDirector') &&
                                !key.includes('dateOfExpiry') &&
                                !key.includes('cessationDate') &&
                                !key.includes('nominatorUen') &&
                                !key.includes('nominatorLegalForm') &&
                                !key.includes('nominatorStatue') &&
                                !key.includes('nominatorRegistrar') &&
                                !key.includes('nominatorJurisdiction') &&
                                !key.includes('nominatorAlias') &&
                                !key.includes('remark') &&
                                !key.includes('generatePOA') &&
                                // new nominator fields
                                !key.includes('isNewNominator') &&
                                !key.includes('nominatorNRIC') &&
                                !key.includes('nominatorDOB') &&
                                !key.includes('nominatorAddress') &&
                                !key.includes('nominatorIdType') &&
                                !key.includes('nominatorNationality') &&
                                !key.includes('nominatorDateOfExpiry') &&
                                !key.includes('newNominatorName') &&
                                !key.includes('nominatorEmail')
                        ).length - 1
                    );

                return (
                    Object.keys(this.directorBluePrint).filter(
                        key => 
                            !!director[key] &&
                            !key.includes('isNomineeDirector') &&
                            !key.includes('dateOfExpiry') &&
                            !key.includes('nominatorAlias') &&
                            !key.includes('cessationDate') &&
                            !key.includes('remark') &&
                            !key.includes('generatePOA') &&
                            // new nominator fields
                            !key.includes('isNewNominator') &&
                            !key.includes('nominatorNRIC') &&
                            !key.includes('nominatorDOB') &&
                            !key.includes('nominatorAddress') &&
                            !key.includes('nominatorIdType') &&
                            !key.includes('nominatorNationality') &&
                            !key.includes('nominatorDateOfExpiry') &&
                            !key.includes('newNominatorName') &&
                            !key.includes('nominatorEmail')
                    ).length - 1
                );
            }
               
                
            return (
                Object.keys(this.directorBluePrint).filter(
                    key => 
                        !!director[key] &&
                        !key.includes('nominatorType') &&
                        !key.includes('isNomineeDirector') &&
                        !key.includes('dateOfExpiry') &&
                        !key.includes('nominatorAlias') &&
                        !key.includes('appointmentDate') &&
                        !key.includes('cessationDate') &&
                        !key.includes('nominatorUen') &&
                        !key.includes('nominatorLegalForm') &&
                        !key.includes('nominatorStatue') &&
                        !key.includes('nominatorRegistrar') &&
                        !key.includes('nominatorJurisdiction') &&
                        !key.includes('remark') &&
                        !key.includes('generatePOA') &&
                        key !== 'nominator' &&
                        // new nominator fields
                        !key.includes('isNewNominator') &&
                        !key.includes('nominatorNRIC') &&
                        !key.includes('nominatorDOB') &&
                        !key.includes('nominatorAddress') &&
                        !key.includes('nominatorIdType') &&
                        !key.includes('nominatorNationality') &&
                        !key.includes('nominatorDateOfExpiry') &&
                        !key.includes('newNominatorName') &&
                        !key.includes('nominatorEmail')
                ).length - 1
            );
        },

        createNomineeDirector(nomineeDirector) {
            this.$emit("createNomineeDirector", nomineeDirector);
        },
    },
};
</script>

<style scoped>
.generate-poa-action .wrapper {
    padding:8px 0px;
}

.generate-poa .label{
    font-weight: 500; 
    margin-right: 5px; 
    font-size: 1.155rem;
}
</style>
